/**
 * Main Image Player orchestrator composable
 * Combines navigation, pan/zoom, and playback info management
 */

import { ref, computed, watch, type Ref, type ComputedRef } from 'vue'
import type {
  ImagePlayerConfig,
  ImagePlayerState,
  ImagePlayerCallbacks,
  ImagePlayerOptions,
  ImageLayout,
  PanZoomState,
} from '../types'
import type { ImageAssetListItem, ImagePlaybackInfo, ImageVariant } from '../../types/asset'
import type { PlayerError } from '../../types/base'
import type { PlaybackInfoState, CollectionItem } from '../../types/player'
import { usePanZoom, type UsePanZoomReturn } from './usePanZoom'
import { useImageNavigation, type UseImageNavigationReturn } from './useImageNavigation'
import { selectBestVariant, type ContainerDimensions } from '../utils/image-utils'

export interface UseImagePlayerOptions extends ImagePlayerOptions {
  callbacks?: ImagePlayerCallbacks
}

export interface UseImagePlayerReturn {
  // Items
  items: Ref<CollectionItem<ImageAssetListItem>[]>
  totalItems: ComputedRef<number>

  // Current item
  currentIndex: Ref<number>
  currentItem: ComputedRef<CollectionItem<ImageAssetListItem> | undefined>
  currentAsset: ComputedRef<ImageAssetListItem | undefined>
  currentImageUrl: ComputedRef<string | undefined>
  currentVariant: ComputedRef<ImageVariant | undefined>

  // Navigation
  canGoNext: ComputedRef<boolean>
  canGoPrev: ComputedRef<boolean>
  goToIndex: (index: number) => void
  next: () => void
  prev: () => void
  first: () => void
  last: () => void

  // Layout
  layout: Ref<ImageLayout>
  setLayout: (layout: ImageLayout) => void

  // Pan/Zoom
  panZoom: ComputedRef<PanZoomState>
  zoomIn: () => void
  zoomOut: () => void
  setZoom: (level: number) => void
  resetZoom: () => void
  setPan: (x: number, y: number) => void
  applyPanDelta: (deltaX: number, deltaY: number) => void
  resetPanZoom: () => void

  // Slideshow
  slideshowActive: Ref<boolean>
  slideshowPaused: Ref<boolean>
  startSlideshow: () => void
  stopSlideshow: () => void
  toggleSlideshow: () => void
  pauseSlideshow: () => void
  resumeSlideshow: () => void

  // Fullscreen
  isFullscreen: Ref<boolean>
  toggleFullscreen: () => void

  // Loading
  isCurrentImageLoading: Ref<boolean>
  loadedImageUids: Ref<Set<string>>
  preloadedImageUids: Ref<Set<string>>
  failedImageUids: Ref<Set<string>>

  // Playback info management
  getPlaybackInfo: (uid: string) => ImagePlaybackInfo | undefined
  setPlaybackInfo: (uid: string, info: ImagePlaybackInfo) => void
  requestPlaybackInfo: (uid: string) => void

  // Error
  error: Ref<PlayerError | undefined>
  clearError: () => void

  // Container size (for variant selection)
  updateContainerSize: (dimensions: ContainerDimensions) => void
  setImageDimensions: (dimensions: { width: number; height: number }) => void

  // Actions
  download: () => void

  // State
  state: ComputedRef<ImagePlayerState>

  // Lifecycle
  destroy: () => void
}

/**
 * Main Image Player orchestrator
 */
export function useImagePlayer(options: UseImagePlayerOptions): UseImagePlayerReturn {
  const { config, initialPlaybackInfos, callbacks } = options

  // Items state (CollectionItem wraps ImageAssetListItem)
  const items = ref<CollectionItem<ImageAssetListItem>[]>(config.items || [])

  // Layout state
  const layout = ref<ImageLayout>(config.layout || 'carousel')

  // Fullscreen state
  const isFullscreen = ref(false)

  // Loading state
  const isCurrentImageLoading = ref(false)
  const loadedImageUids = ref<Set<string>>(new Set())
  const preloadedImageUids = ref<Set<string>>(new Set())
  const failedImageUids = ref<Set<string>>(new Set())

  // Error state
  const error = ref<PlayerError | undefined>()

  // Playback info cache
  const playbackInfoCache = ref<Map<string, ImagePlaybackInfo>>(
    initialPlaybackInfos ? new Map(initialPlaybackInfos) : new Map(),
  )

  // Container dimensions for variant selection
  const containerDimensions = ref<ContainerDimensions>({ width: 800, height: 600 })

  // Extract assets from collection items for the navigation composable
  const assetItems = computed(() => items.value.map((item) => item.asset))

  // Initialize navigation composable
  const navigation: UseImageNavigationReturn = useImageNavigation({
    items: assetItems,
    initialIndex: config.currentIndex || 0,
    loop: config.enableSlideshow !== false,
    shuffle: false, // Could be made configurable
    slideshowDuration: config.slideshowDuration || 5,
    onIndexChange: (index) => {
      const item = items.value[index]
      if (item) {
        callbacks?.onItemChange?.(index, item)
      }
      // Reset pan/zoom when changing images
      panZoomComposable.reset()
      // Load playback info for new current item
      loadCurrentPlaybackInfo()
      // Preload adjacent images
      preloadAdjacentImages()
    },
    onSlideshowToggle: (active) => {
      callbacks?.onSlideshowToggle?.(active)
    },
    onSlideshowPause: (paused) => {
      callbacks?.onSlideshowPause?.(paused)
    },
  })

  // Initialize pan/zoom composable
  const panZoomComposable: UsePanZoomReturn = usePanZoom({
    initialZoom: 1,
    minZoom: 1,
    maxZoom: config.maxZoom || 4,
    onZoomChange: (state) => {
      callbacks?.onZoomChange?.(state)
    },
    onPanChange: (panX, panY) => {
      callbacks?.onPanChange?.(panX, panY)
    },
  })

  // Computed
  const totalItems = computed(() => items.value.length)

  const currentItem = computed(() => items.value[navigation.currentIndex.value])

  const currentAsset = computed(() => currentItem.value?.asset)

  const currentPlaybackInfo = computed(() => {
    const asset = currentAsset.value
    if (!asset) return undefined
    return playbackInfoCache.value.get(asset.uid)
  })

  const currentVariant = computed(() => {
    const info = currentPlaybackInfo.value
    if (!info?.variants) return undefined
    return selectBestVariant(
      info.variants,
      containerDimensions.value,
      config.preferredSize || 'large',
    )
  })

  const currentImageUrl = computed(() => {
    return currentVariant.value?.url?.url
  })

  // Playback info state for external access
  const playbackInfoState = computed<PlaybackInfoState>(() => ({
    isLoading: isCurrentImageLoading.value,
    isLoaded: !!currentPlaybackInfo.value,
  }))

  // Full state
  const state = computed<ImagePlayerState>(() => ({
    currentIndex: navigation.currentIndex.value,
    layout: layout.value,
    isFullscreen: isFullscreen.value,
    panZoom: panZoomComposable.state.value,
    slideshowActive: navigation.slideshowActive.value,
    slideshowPaused: navigation.slideshowPaused.value,
    isCurrentImageLoading: isCurrentImageLoading.value,
    loadedImageUids: loadedImageUids.value,
    preloadedImageUids: preloadedImageUids.value,
    error: error.value,
    failedImageUids: failedImageUids.value,
    playbackInfo: playbackInfoState.value,
  }))

  /**
   * Loads playback info for the current item
   */
  async function loadCurrentPlaybackInfo(): Promise<void> {
    const asset = currentAsset.value
    if (!asset) return

    // If already loaded, mark as not loading
    if (playbackInfoCache.value.has(asset.uid)) {
      isCurrentImageLoading.value = false
      callbacks?.onImageLoad?.(asset.uid)
      return
    }

    // Request playback info via callback
    if (callbacks?.onPlaybackInfoNeeded) {
      isCurrentImageLoading.value = true
      try {
        const info = await callbacks.onPlaybackInfoNeeded(asset.uid as any)
        if (info && info.type === 'image') {
          setPlaybackInfo(asset.uid, info as ImagePlaybackInfo)
        } else {
          isCurrentImageLoading.value = false
        }
      } catch (err) {
        isCurrentImageLoading.value = false
        setError(asset.uid, {
          code: 'PLAYBACK_INFO_FETCH_FAILED',
          message: err instanceof Error ? err.message : 'Failed to load image info',
          recoverable: true,
        })
      }
    } else {
      isCurrentImageLoading.value = false
    }
  }

  /**
   * Preloads playback info for adjacent images
   */
  function preloadAdjacentImages(): void {
    const index = navigation.currentIndex.value
    const adjacentIndices = [index - 1, index + 1]

    for (const adjIndex of adjacentIndices) {
      if (adjIndex >= 0 && adjIndex < items.value.length) {
        const item = items.value[adjIndex]
        const asset = item?.asset
        if (asset && !playbackInfoCache.value.has(asset.uid) && !preloadedImageUids.value.has(asset.uid)) {
          // Mark as being preloaded
          preloadedImageUids.value.add(asset.uid)
          // Request via callback
          if (callbacks?.onPlaybackInfoNeeded) {
            callbacks.onPlaybackInfoNeeded(asset.uid as any).then((info) => {
              if (info && info.type === 'image') {
                setPlaybackInfo(asset.uid, info as ImagePlaybackInfo)
              }
            }).catch(() => {
              // Silently fail preload
            })
          }
        }
      }
    }
  }

  // Playback info management
  function getPlaybackInfo(uid: string): ImagePlaybackInfo | undefined {
    return playbackInfoCache.value.get(uid)
  }

  function setPlaybackInfo(uid: string, info: ImagePlaybackInfo): void {
    playbackInfoCache.value.set(uid, info)
    loadedImageUids.value.add(uid)

    // If this is the current item, mark as loaded
    if (currentAsset.value?.uid === uid) {
      isCurrentImageLoading.value = false
      callbacks?.onImageLoad?.(uid)
    }
  }

  function requestPlaybackInfo(uid: string): void {
    if (!playbackInfoCache.value.has(uid) && callbacks?.onPlaybackInfoNeeded) {
      callbacks.onPlaybackInfoNeeded(uid as any).then((info) => {
        if (info && info.type === 'image') {
          setPlaybackInfo(uid, info as ImagePlaybackInfo)
        }
      }).catch(() => {
        // Handle error
      })
    }
  }

  // Layout management
  function setLayout(newLayout: ImageLayout): void {
    if (newLayout !== layout.value) {
      layout.value = newLayout
      callbacks?.onLayoutChange?.(newLayout)
    }
  }

  // Fullscreen
  function toggleFullscreen(): void {
    isFullscreen.value = !isFullscreen.value
    callbacks?.onFullscreenChange?.(isFullscreen.value)
  }

  // Error management
  function clearError(): void {
    error.value = undefined
  }

  function setError(uid: string, err: PlayerError): void {
    error.value = err
    failedImageUids.value.add(uid)
    callbacks?.onImageError?.(uid, err)
    callbacks?.onError?.(err)
  }

  // Container size
  function updateContainerSize(dimensions: ContainerDimensions): void {
    containerDimensions.value = dimensions
    panZoomComposable.setContainerDimensions(dimensions)
  }

  // Actions
  function download(): void {
    const asset = currentAsset.value
    if (asset) {
      callbacks?.onDownload?.(asset.uid)
    }
  }

  // Lifecycle
  function destroy(): void {
    navigation.stopSlideshow()
    playbackInfoCache.value.clear()
    loadedImageUids.value.clear()
    preloadedImageUids.value.clear()
    failedImageUids.value.clear()
  }

  // Watch for items changes
  watch(
    () => config.items,
    (newItems) => {
      if (newItems) {
        items.value = newItems
      }
    },
    { deep: true },
  )

  // Initial load
  if (items.value.length > 0) {
    loadCurrentPlaybackInfo()
  }

  return {
    // Items
    items,
    totalItems,

    // Current item
    currentIndex: navigation.currentIndex,
    currentItem,
    currentAsset,
    currentImageUrl,
    currentVariant,

    // Navigation
    canGoNext: navigation.canGoNext,
    canGoPrev: navigation.canGoPrev,
    goToIndex: navigation.goToIndex,
    next: navigation.next,
    prev: navigation.prev,
    first: navigation.first,
    last: navigation.last,

    // Layout
    layout,
    setLayout,

    // Pan/Zoom
    panZoom: panZoomComposable.state,
    zoomIn: panZoomComposable.zoomIn,
    zoomOut: panZoomComposable.zoomOut,
    setZoom: panZoomComposable.setZoom,
    resetZoom: panZoomComposable.resetZoom,
    setPan: panZoomComposable.setPan,
    applyPanDelta: panZoomComposable.applyPanDelta,
    resetPanZoom: panZoomComposable.reset,

    // Slideshow
    slideshowActive: navigation.slideshowActive,
    slideshowPaused: navigation.slideshowPaused,
    startSlideshow: navigation.startSlideshow,
    stopSlideshow: navigation.stopSlideshow,
    toggleSlideshow: navigation.toggleSlideshow,
    pauseSlideshow: navigation.pauseSlideshow,
    resumeSlideshow: navigation.resumeSlideshow,

    // Fullscreen
    isFullscreen,
    toggleFullscreen,

    // Loading
    isCurrentImageLoading,
    loadedImageUids,
    preloadedImageUids,
    failedImageUids,

    // Playback info
    getPlaybackInfo,
    setPlaybackInfo,
    requestPlaybackInfo,

    // Error
    error,
    clearError,

    // Container size
    updateContainerSize,
    setImageDimensions: panZoomComposable.setImageDimensions,

    // Actions
    download,

    // State
    state,

    // Lifecycle
    destroy,
  }
}
